import { useEffect, useState } from "react";
import { Truck, Menu, X, Instagram } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { WHATSAPP_URL, INSTAGRAM_URL } from "@/lib/constants";

const NAV = [
  { label: "Chi Siamo", href: "#chi-siamo" },
  { label: "Servizi", href: "#servizi" },
  { label: "Vantaggi", href: "#vantaggi" },
  { label: "Preventivo", href: "#preventivo" },
  { label: "Contatti", href: "#contatti" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#hero"
          data-testid="header-logo"
          className="flex items-center gap-3 group"
        >
          <div className="relative flex h-10 w-10 items-center justify-center rounded-md bg-gradient-to-br from-[#0055ff] to-[#001a4d] border border-blue-400/30 shadow-[0_0_20px_rgba(0,85,255,0.5)] group-hover:shadow-[0_0_30px_rgba(0,229,255,0.7)] transition-shadow">
            <Truck className="h-5 w-5 text-white" strokeWidth={2.4} />
          </div>
          <div className="leading-tight">
            <div className="font-heading text-[15px] md:text-base font-black tracking-[0.18em] uppercase text-white">
              Bennici
            </div>
            <div className="font-heading text-[10px] md:text-[11px] tracking-[0.32em] uppercase text-blue-300/80">
              Autotrasporti · Canicattì
            </div>
          </div>
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-[13px] font-medium uppercase tracking-[0.16em] text-gray-300 hover:text-white transition-colors relative group"
            >
              {item.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gradient-to-r from-[#0055ff] to-[#00e5ff] group-hover:w-full transition-all duration-500" />
            </a>
          ))}
        </nav>

        {/* CTA + mobile toggle */}
        <div className="flex items-center gap-3">
          <a
            data-testid="header-instagram"
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram Bennici Autotrasporti"
            className="hidden sm:inline-flex h-10 w-10 items-center justify-center border border-white/10 hover:border-pink-400/70 text-white/80 hover:text-white transition-all group relative overflow-hidden"
          >
            <span className="absolute inset-0 bg-gradient-to-tr from-[#f09433] via-[#dc2743] to-[#bc1888] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <Instagram size={16} className="relative z-10" strokeWidth={2} />
          </a>
          <a
            data-testid="header-cta-whatsapp"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.16em] text-white border border-blue-500/50 bg-blue-600/10 hover:bg-blue-600 hover:border-blue-400 transition-all duration-300 shadow-[0_0_15px_rgba(0,85,255,0.4)] hover:shadow-[0_0_25px_rgba(0,229,255,0.7)]"
          >
            Preventivo
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="md:hidden flex h-10 w-10 items-center justify-center rounded-md border border-white/10 text-white hover:border-blue-400/60"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/5"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className="text-sm font-medium uppercase tracking-[0.16em] text-gray-300 hover:text-blue-400"
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </a>
              ))}
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-[0.16em] text-gray-300 hover:text-pink-400"
                data-testid="mobile-nav-instagram"
              >
                <Instagram size={14} />
                Instagram
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
